import React, { useContext, useEffect, useMemo, useReducer } from "react";
import { graphql } from "react-relay";
import { NavLink } from "react-router-dom";

import { faCalendar, faStreetView } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import AppContext from "~/common/AppContext";
import { markAsRead } from "~/feedback/FeedbackItem";
import { FeedbackContext } from "~/nav/panels/Feedback";
import Avatar from "~/ui/legacy/Avatar";

import { FEEDBACK_CATEGORY_VALUES } from "./CategoryDropdown";
import FeedbackCardActions from "./FeedbackCardActions";
import type { FeedbackCardType } from "./types";

import styles from "./FeedbackCard.module.css";

graphql`
	fragment FeedbackCardData on FeedbackNode {
		id
		title
		type
		isDraft
		isMeetingFeedback
		requestCount
		responseCount
		sharedWithMe
		updatedAt
		isArchived
		isThreeSixtyParent
		isAnonymous

		parent {
			id
		}

		creator {
			id
			email
			image
			fullName
			firstName
			lastName
		}

		recipients {
			user {
				id
			}
		}

		requests {
			user {
				id
				firstName
			}
		}

		context {
			__typename
			... on User {
				id
				email
				image
				fullName
				firstName
				lastName
				isGuest
				relationshipToViewer
			}
			... on Event {
				id
				title
				start
				end
			}
			... on Note {
				id
				title
				eventStart
				eventEnd
				url
			}
			... on OtherContext {
				name
				emoji
			}
		}
	}
`;

graphql`
	fragment FeedbackCard on FeedbackOrRequest {
		__typename

		... on FeedbackNode {
			lastSeenByUser
			...FeedbackCardData @relay(mask: false)
		}
		... on FeedbackRequestNode {
			id
			isArchived
			lastSeenByUser
			hasDraftResponse
			complete

			feedbackRecord {
				id
			}
			feedback {
				...FeedbackCardData @relay(mask: false)
			}
		}
	}
`;

const dateFormats = {
	lastDay: "[Yesterday]",
	lastWeek: "[Last] dddd",
	sameElse: "MMM D, YYYY",
};

interface FeedbackCardProps {
	feedback: FeedbackCardType;
	refetch(): void;
}

const ONE_MINUTE_IN_MILLISECONDS = 60000;
const ONE_HOUR_IN_MILLISECONDS = ONE_MINUTE_IN_MILLISECONDS * 60;
const ONE_DAY_IN_MILLISECONDS = ONE_HOUR_IN_MILLISECONDS * 24;

export default function FeedbackCard({ feedback, refetch }: FeedbackCardProps) {
	const { user } = useContext(AppContext);
	const { view } = React.useContext(FeedbackContext);

	const isRequest = feedback.__typename === "FeedbackRequestNode";
	const feedbackData = isRequest ? feedback.feedback : feedback;
	const {
		updatedAt,
		isArchived,
		context,
		creator,
		sharedWithMe,
		isDraft,
		requests,
		isAnonymous,
		id: feedbackNodeId,
	} = feedbackData;
	const lastSeenByUser = feedback.lastSeenByUser;
	const contextData = context ? context[0] : null;
	const [timeUpdated, forceTimeUpdate] = useReducer(x => x + 1, 0);

	const destination = isRequest
		? `/feedback/request/${feedback.id}/${feedback.feedbackRecord ? "" : "respond/"}`
		: contextData?.__typename === "Note" && contextData?.url
			? `${contextData.url}feedback/?intendToSeeFeedback=${feedbackNodeId}`
			: `/feedback/${feedback.id}/${isDraft ? "draft/" : ""}`;

	const header = useMemo(() => {
		let creatorFirstName = creator?.firstName ?? "";

		if (creatorFirstName.includes("anon_")) {
			creatorFirstName = "Anonymous";
		}

		if (contextData?.__typename === "Note") {
			if (isRequest) {
				return "You gave meeting feedback";
			} else if (isAnonymous) {
				return "Anonymous gave meeting feedback";
			} else {
				const numResponses = requests.length;
				// A feedback should always have requests, but in some weird cases it doesn't
				// While we don't figure out why that is happening we will handle this here
				if (numResponses === 0) {
					return "You gave meeting feedback";
				}
				const name = user?.id === requests[0].user.id ? "You" : requests[0].user.firstName;
				return numResponses === 1
					? `${name} gave meeting feedback`
					: `${name}, ${numResponses - 1}+ gave meeting feedback`;
			}
		}

		if (isRequest) {
			if (feedback.feedbackRecord) {
				return "You responded";
			}
			if (feedback.hasDraftResponse) {
				return "Your draft response";
			}
			return `${creatorFirstName} wants your feedback`;
		}
		if (sharedWithMe) {
			return `${creatorFirstName} shared feedback`;
		}
		if (feedbackData.type === "UNSOLICITED") {
			if (isDraft) {
				return "Your draft feedback";
			}
			return user && creator?.id === user.id ? "You gave feedback" : `${creatorFirstName} gave you feedback`;
		}
		if (isDraft) {
			return "Your draft request";
		}
		return "You requested feedback";
	}, [isRequest, sharedWithMe, creator, user, isDraft, feedbackData, feedback, contextData, requests, isAnonymous]);

	const showUnreadDot = useMemo(() => {
		if (isRequest) {
			if (feedback.feedbackRecord || feedback.isArchived) {
				return false;
			}

			// We show the unread dot if we received the request and haven't look at it yet
			return !lastSeenByUser;
		}

		// We show the unread dot if there's an update and we are the ones receiving the response
		return !isArchived && !isDraft && (!lastSeenByUser || moment(lastSeenByUser).isSameOrBefore(updatedAt || ""));
	}, [isArchived, isDraft, lastSeenByUser, updatedAt, isRequest, feedback]);

	const formattedDate = useMemo(() => {
		const now = moment();
		const momentDate = moment(updatedAt);
		if (!momentDate.isSame(now, "day")) {
			return momentDate.calendar(undefined, dateFormats);
		}
		const hoursDifference = now.diff(momentDate, "hours");
		if (hoursDifference > 0) {
			return `${hoursDifference}h ago`;
		}
		const minutesDifference = now.diff(momentDate, "minutes");
		return minutesDifference > 0 ? `${minutesDifference}m ago` : "Just now";
		// We want to take timeUpdated into consideration to reevaluate our memoized value
		// So we can reevaluate based of the interval in the useEffect
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeUpdated, updatedAt]);

	useEffect(() => {
		const momentDate = moment(updatedAt);
		const timeDifference = moment.duration(moment().diff(momentDate));
		if (timeDifference.asDays() > 1) {
			const timeUpdateInterval = setInterval(forceTimeUpdate, ONE_DAY_IN_MILLISECONDS);
			return () => clearInterval(timeUpdateInterval);
		}
		if (timeDifference.asHours() > 1) {
			const timeUpdateInterval = setInterval(forceTimeUpdate, ONE_HOUR_IN_MILLISECONDS);
			return () => clearInterval(timeUpdateInterval);
		}
		const timeUpdateInterval = setInterval(forceTimeUpdate, ONE_MINUTE_IN_MILLISECONDS);
		return () => clearInterval(timeUpdateInterval);
	}, [updatedAt]);

	const titleIcon = useMemo(() => {
		if (!contextData) {
			return null;
		}
		if (contextData.__typename === "Event" || contextData.__typename === "Note") {
			return <FontAwesomeIcon icon={faCalendar} />;
		}
		if (contextData.__typename === "User") {
			return (
				<>
					<Avatar user={contextData} link={false} className={styles.typeIcon} />
					{(feedbackData.isThreeSixtyParent || feedbackData.parent) && <FontAwesomeIcon icon={faStreetView} />}
				</>
			);
		}
		if (contextData.__typename == "OtherContext") {
			return <div className={styles.emojiIcon}>{contextData.emoji}</div>;
		}
		return null;
	}, [contextData, feedbackData]);

	const title = useMemo(() => {
		if (feedbackData.title) {
			return feedbackData.title;
		}
		if (contextData) {
			if (contextData.__typename === "User") {
				return contextData.fullName;
			}
			if (contextData.__typename === "Event" || contextData.__typename === "Note") {
				return contextData.title;
			}
		}
		return null;
	}, [feedbackData, contextData]);

	const badges = useMemo(() => {
		const items = [];
		if (!isRequest && !feedbackData.isDraft && feedback.requestCount > 0) {
			if (contextData?.__typename !== "Note" || !contextData?.eventStart) {
				const feedbackText = `${feedback.responseCount}/${feedback.requestCount} responses`;
				if (feedback.responseCount === feedback.requestCount) {
					items.push({
						content: feedbackText,
						additionalClasses: styles.green,
					});
				} else {
					items.push({
						content: feedbackText,
					});
				}
			}
		} else if (isRequest && !feedback.feedbackRecord) {
			items.push({
				content: "Needs action",
				additionalClasses: styles.blue,
			});
		}
		if (feedbackData.isDraft || (isRequest && feedback.hasDraftResponse)) {
			items.push({
				content: "Draft",
				additionalClasses: styles.orange,
			});
		}
		if (items.length === 0) {
			return null;
		}
		return (
			<>
				{items.map(({ content, additionalClasses }) => (
					<span key={`badge-${content}`} className={cx(styles.badge, additionalClasses)}>
						{content}
					</span>
				))}
			</>
		);
	}, [feedback, feedbackData, isRequest, contextData]);

	const showCardActions = view !== FEEDBACK_CATEGORY_VALUES.FEEDBACK_SENT && (!feedback.isArchived || isRequest);

	const markFeedbackAsRead = React.useCallback(() => {
		/*
		 * This is a special case for Meeting Feedback cards because they are a "stack", and clicking on them
		 * doesn't mark the item as unread because there is an intermediate page (Stream > Feedback tab).
		 */
		if (feedback.id) {
			markAsRead(feedback.id);
		}
	}, [feedback]);

	return (
		<NavLink
			to={destination}
			className={cx(styles.card, { [styles.unread]: showUnreadDot })}
			activeClassName={styles.active}
			data-test-id="panel-feedback-list-card"
			onClick={contextData?.__typename === "Note" ? markFeedbackAsRead : undefined}
		>
			<div className={styles.header}>
				{showUnreadDot && (
					<div className={styles.unreadDotContainer}>
						<span className={styles.unreadDot} />
					</div>
				)}
				<h2 title={header}>{header}…</h2>
				<span>{formattedDate}</span>
			</div>
			{/* We need the actions button to come right before the h3 so we can use a css selector
					to wrap the title's width when the action button is active and not being hover */}
			{showCardActions && (
				<FeedbackCardActions
					feedback={feedback}
					triggerClassName={styles.actions}
					read={!showUnreadDot}
					refetch={refetch}
				/>
			)}
			<h3
				className={cx({ [styles.untitled]: !title, [styles.cutOnHover]: !badges && showCardActions })}
				title={title || "Untitled"}
			>
				{titleIcon}
				{title || "Untitled"}
			</h3>
			{badges}
		</NavLink>
	);
}
